import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { type SentryErrorBatcher, createSentryErrorBatcher } from "@app/config/loggers/sentryErrorBatcher";
import { FileUploadIdentifier, fetchPresignedUrl, updateFileUploadResult } from "@base/api/pre-signed-url";
import { hasHeicExtension, isHeicFile, isHeicWithMissingMime } from "@base/lib/isHeicFile";
import {
  type ImageUploadItem,
  type ImageUploadQueue,
  createImageUploadQueue,
  isAcceptedFile,
  useImageUploadQueue,
} from "@base/model/hooks";
import { type ImageItem, type ImageUploadState, extractWebpThumbnail } from "@features/image-gallery/model/imageItem";
import { useImageGalleryStore } from "@features/image-gallery/model/useImageGalleryStore";
import * as Sentry from "@sentry/react";
import { useStore } from "@tanstack/react-form";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import type { DailyLogForm } from "./form";

export const MAX_IMAGES = 30;
export const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

/**
 * 큐에 받아들일 파일 종류. `ImageUploadInput` 의 `accept` 기본값과 동일하게 유지한다.
 *
 * MIME(`image/*`) 만으로 판정하면 브라우저가 이미지 MIME 를 못 붙인 HEIC 가 조용히 탈락한다.
 * iOS 에서 고른 파일과 Windows(HEIF Image Extensions 미설치) 에서 고른 파일이 여기 해당한다.
 * 확장자 패턴을 함께 두어 그런 파일도 큐에 들어오게 한다 — {@link isHeicWithMissingMime} 참고.
 */
export const ACCEPTED_IMAGE_TYPES = ["image/*", ".heic", ".heif"];

/** 계측용 확장자 추출. 확장자가 없으면 `(none)`. */
function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "(none)";
}

/** 계측 프로퍼티가 무한정 커지지 않게 하는 상한. */
const MAX_REPORTED_FILES = 20;

type UploadFailureContext = { name: string; size: number; type: string };

/** 파일 1장의 업로드 시도 이력. 소요 시간과 재시도 횟수를 계산하는 데 쓴다. */
type UploadAttempt = { startedAt: number; attempt: number };

/**
 * 업로드 이벤트를 프로젝트·드래프트에 묶기 위한 컨텍스트.
 *
 * 이게 없으면 "어느 현장에서 업로드가 많이 실패하는가"를 물을 수 없다.
 * 업로드는 작성 퍼널 안에서만 일어나므로 호출부가 항상 알고 있는 값이다.
 */
export type DailyLogImageAnalyticsContext = {
  projectId: string;
  draftId?: string;
};

function uploadProperties(
  item: ImageUploadItem,
  attempt: UploadAttempt | undefined,
  context: DailyLogImageAnalyticsContext,
  uploadSessionId: string | null,
) {
  return {
    project_id: context.projectId,
    draft_id: context.draftId ?? null,
    // 파일 선택 1회를 묶는 키. "10장 골라 8장 성공" 같은 세션 단위 전환율의 조인 키다.
    upload_session_id: uploadSessionId,
    file_size: item.file.size,
    mime_type: item.file.type || "(empty)",
    is_heic: isHeicFile(item.file),
    // 확장자만 HEIC 인 파일이 끝까지 업로드됐는지 추적한다 (MIME 누락 가설의 종단 확인).
    is_heic_missing_mime: isHeicWithMissingMime(item.file),
    duration_ms: attempt ? Math.round(performance.now() - attempt.startedAt) : null,
    attempt: attempt?.attempt ?? 1,
  };
}

export interface DailyLogImageController {
  /** seq 정렬된 갤러리 아이템 (그리드 렌더 입력). */
  items: ImageItem[];
  /** 20MB 필터 + 30장 상한을 적용해 업로드 시작. */
  addFiles: (files: FileList | File[]) => void;
  /** 아이템 제거 (업로드 중이면 XHR abort + blob revoke, 그 외엔 스토어에서 제거). */
  removeItem: (id: string) => void;
  /** 업로드 실패 아이템 재시도. */
  retryItem: (id: string) => void;
  /** 서버 이미지가 준비된 uploaded 아이템의 로컬 preview blob 을 해제. */
  releaseLocalPreview: (id: string, localPreviewUrl: string) => void;
  /** 더 추가할 수 없는 상태 (상한 도달). */
  isAtMax: boolean;
}

/** 업로드 큐 아이템의 상태를 갤러리 아이템의 upload 상태로 매핑. success 는 곧 finalize 되므로 uploading 으로 본다. */
function toUploadState(item: ImageUploadItem): ImageUploadState {
  if (item.status === "error") {
    return { status: "error", progress: item.progress, error: item.error };
  }
  return { status: item.status === "pending" ? "pending" : "uploading", progress: item.progress };
}

function sameUrls(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((url, i) => url === b[i]);
}

/**
 * 일일 로그 이미지 업로드 컨트롤러.
 *
 * 큐(transport) + 갤러리 스토어(단일 진실) 를 소유하고, 둘을 잇는다:
 * - 업로드 큐 아이템을 스토어 uploading 아이템으로 동기화 (진행률/상태).
 * - 성공 아이템을 finalize: `updateFileUploadResult` → webp 추출 → `markUploaded` → 큐에서 제거.
 *   단, 로컬 preview blob 은 서버 이미지가 실제 표시 가능해질 때까지 스토어가 보유한다.
 *   (콜백 wiring 대신 큐 items 를 관찰하는 effect 라 `finalizeRef` latest-closure 해킹이 불필요.)
 * - 스토어의 committedUrls 를 form.image_urls 로 projection.
 *
 * 기존 `SitePictureStep` + `useDailyLogImageUploadFinalizer` 의 책임을 합친다.
 */
export function useDailyLogImageController(
  form: DailyLogForm,
  analytics: DailyLogImageAnalyticsContext,
): DailyLogImageController {
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;
  /** 업로드 아이템 → 그 아이템이 속한 선택 세션. 아이템이 큐에서 빠질 때 함께 지운다. */
  const uploadSessionsRef = useRef(new Map<string, string>());
  const [initialUrls] = useState<string[]>(() => form.getFieldValue("image_urls") ?? []);
  const store = useImageGalleryStore(initialUrls);
  const { items, committedUrls, syncCommittedUrls, upsertUploading, markUploaded, clearLocalPreview, remove } = store;
  const formImageUrls = useStore(form.store, (s) => s.values.image_urls);
  const lastProjectedUrlsRef = useRef<string[]>(initialUrls);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // 언마운트 후 비동기 finalize 응답으로 setState 하는 것을 막는 가드.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      for (const item of itemsRef.current) {
        if (item.lifecycle === "uploaded" && item.localPreviewUrl) {
          URL.revokeObjectURL(item.localPreviewUrl);
        }
      }
      // 진행 중인 XHR 취소 + 큐에 남은 (uploading/pending) preview blob 해제.
      uploadQueueRef.current?.destroy();
    };
  }, []);

  const errorBatcherRef = useRef<SentryErrorBatcher<UploadFailureContext> | null>(null);
  if (!errorBatcherRef.current) {
    errorBatcherRef.current = createSentryErrorBatcher<UploadFailureContext>({
      buildExtra: (files) => ({ failedCount: files.length, files }),
    });
  }
  useEffect(() => {
    return () => {
      errorBatcherRef.current?.destroy();
    };
  }, []);

  // 업로드 성공률의 분모(= 시도 수)와 장당 소요 시간을 남기기 위한 시도 이력.
  const uploadAttemptsRef = useRef<Map<string, UploadAttempt>>(new Map());

  const uploadQueueRef = useRef<ImageUploadQueue | null>(null);
  if (!uploadQueueRef.current) {
    uploadQueueRef.current = createImageUploadQueue({
      accept: ACCEPTED_IMAGE_TYPES,
      maxFiles: MAX_IMAGES,
      getPresignedUrl: async ({ id, file }) => {
        const { presigned_urls } = await fetchPresignedUrl({
          file_infos: [{ file_id: id, file_name: file.name }],
          file_upload_identifier: FileUploadIdentifier.BRICKS_DAILY_LOG_IMAGE,
        });
        const presignedUrl = presigned_urls[0];
        if (!presignedUrl) {
          throw new Error("Failed to retrieve presigned URL from server response");
        }
        const { url, file_upload_path } = presignedUrl;
        return { uploadUrl: url, fileUrl: file_upload_path };
      },
      onItemStart: (item) => {
        const previous = uploadAttemptsRef.current.get(item.id);
        uploadAttemptsRef.current.set(item.id, {
          startedAt: performance.now(),
          attempt: (previous?.attempt ?? 0) + 1,
        });
      },
      onItemComplete: (item) => {
        EventLogger.result(
          CLIENT_EVENT.IMAGE_UPLOAD_SUCCESS,
          uploadProperties(
            item,
            uploadAttemptsRef.current.get(item.id),
            analyticsRef.current,
            uploadSessionsRef.current.get(item.id) ?? null,
          ),
        );
        uploadAttemptsRef.current.delete(item.id);
        uploadSessionsRef.current.delete(item.id);
      },
      onError: (error, item) => {
        EventLogger.result(CLIENT_EVENT.IMAGE_UPLOAD_FAIL, {
          ...uploadProperties(
            item,
            uploadAttemptsRef.current.get(item.id),
            analyticsRef.current,
            // 실패한 아이템은 재시도될 수 있으므로 세션 매핑을 지우지 않는다.
            uploadSessionsRef.current.get(item.id) ?? null,
          ),
          error_message: error.message,
        });

        errorBatcherRef.current?.report(error, {
          name: item.file.name,
          size: item.file.size,
          type: item.file.type,
        });
      },
    });
  }
  const uploadQueue = uploadQueueRef.current;
  const { items: queueItems } = useImageUploadQueue(uploadQueue);

  // S3 업로드 성공 → 백엔드 통지 + webp 추출 + 스토어 승격 + 큐에서 제거.
  const finalize = useCallback(
    async (item: ImageUploadItem): Promise<void> => {
      if (!item.uploadedUrl) return;
      const isHeic = isHeicFile(item.file);

      let webpUrl: string | undefined;
      try {
        const response = await updateFileUploadResult({
          file_upload_results: [{ file_upload_path: item.uploadedUrl, is_success: item.status === "success" }],
        });
        const result = response[0];
        if (result) webpUrl = extractWebpThumbnail(result.post_process_result, isHeic);
      } catch {
        // 썸네일 추출 실패 → 원본으로 낙착. 업로드 자체는 성공 처리.
      }

      if (!aliveRef.current) return;
      markUploaded(item.id, { originalUrl: item.uploadedUrl, webpUrl });
      uploadQueue.removeItem(item.id, { revokePreviewUrl: false });
    },
    [markUploaded, uploadQueue],
  );

  // 큐 아이템을 스토어로 동기화 + 신규 성공 아이템 finalize (멱등).
  const finalizedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const q of queueItems) {
      if (finalizedIdsRef.current.has(q.id)) continue;

      upsertUploading({
        id: q.id,
        isHeic: isHeicFile(q.file),
        file: q.file,
        localPreviewUrl: q.previewUrl,
        upload: toUploadState(q),
      });

      if (q.status === "success" && q.uploadedUrl) {
        finalizedIdsRef.current.add(q.id);
        void finalize(q);
      }
    }
  }, [queueItems, upsertUploading, finalize]);

  // 외부 form 변경(draft restore 등)은 스토어로 흡수하고, 내부 스토어 변경은 form 으로 projection 한다.
  useEffect(() => {
    const current = formImageUrls ?? [];
    const changedOutsideController = !sameUrls(current, lastProjectedUrlsRef.current);

    if (changedOutsideController && !sameUrls(current, committedUrls)) {
      lastProjectedUrlsRef.current = current;
      syncCommittedUrls(current);
      return;
    }

    if (!sameUrls(current, committedUrls)) {
      lastProjectedUrlsRef.current = committedUrls;
      form.setFieldValue("image_urls", committedUrls);
    }
  }, [committedUrls, form, formImageUrls, syncCommittedUrls]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      // 파일 선택 1회 = 세션 1개. 거부/시작/성공/실패를 한 줄로 잇는 키다.
      // 주의: Mixpanel 퍼널은 유저 또는 등록된 group key 로만 이을 수 있다.
      // 이 키로 세션 단위 전환율을 보려면 raw export 나 웨어하우스에서 집계해야 한다.
      const uploadSessionId = uuidv4();
      const currentQueueLength = uploadQueue.getSnapshot().length;
      const committedCount = items.filter((it) => it.lifecycle !== "uploading").length;
      const remaining = MAX_IMAGES - committedCount - currentQueueLength;

      const all = Array.from(files);

      if (remaining <= 0) {
        EventLogger.track(CLIENT_EVENT.IMAGE_UPLOAD_REJECTED, {
          project_id: analyticsRef.current.projectId,
          draft_id: analyticsRef.current.draftId ?? null,
          upload_session_id: uploadSessionId,
          selected_count: all.length,
          rejected_count: all.length,
          rejected_unsupported_type: 0,
          rejected_over_file_size: 0,
          rejected_over_max_images: all.length,
          max_images: MAX_IMAGES,
          max_file_size_bytes: MAX_FILE_SIZE_BYTES,
          rejected_sizes: [],
        });
        return;
      }

      const oversized = all.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
      const valid = all.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);

      if (oversized.length > 0) {
        toast.warning(
          oversized.length === 1
            ? `"${oversized[0]!.name}" exceeds the 20MB limit and was not added.`
            : `${oversized.length} files exceed the 20MB limit and were not added.`,
        );
      }

      const next = valid.slice(0, remaining);
      const droppedByLimit = valid.length - next.length;

      // 큐가 accept 로 한 번 더 걸러내므로 실제 업로드는 이 중 통과분만이다.
      // 관측만 하고 `next` 에서 빼지는 않는다 — 미리 걸러내면 slice 자리 배분이 달라져 동작이 바뀐다.
      const unsupported = next.filter((f) => !isAcceptedFile(f, ACCEPTED_IMAGE_TYPES));
      const queued = next.filter((f) => isAcceptedFile(f, ACCEPTED_IMAGE_TYPES));

      if (unsupported.length > 0) {
        toast.warning(
          unsupported.length === 1
            ? `"${unsupported[0]!.name}" is not a supported image format and was not added.`
            : `${unsupported.length} files are not supported image formats and were not added.`,
        );
      }

      // 한 번의 선택에서 여러 사유로 걸러질 수 있다. 사유별로 이벤트를 나눠 보내면
      // 각 이벤트가 같은 `selected_count` 를 실어 선택 건수가 이중 계상되므로,
      // 선택 1회당 1건으로 묶고 사유는 개수 필드로 나눈다.
      const rejectedCount = oversized.length + droppedByLimit + unsupported.length;
      if (rejectedCount > 0) {
        EventLogger.track(CLIENT_EVENT.IMAGE_UPLOAD_REJECTED, {
          project_id: analyticsRef.current.projectId,
          draft_id: analyticsRef.current.draftId ?? null,
          upload_session_id: uploadSessionId,
          selected_count: all.length,
          rejected_count: rejectedCount,
          rejected_unsupported_type: unsupported.length,
          rejected_over_file_size: oversized.length,
          rejected_over_max_images: droppedByLimit,
          max_images: MAX_IMAGES,
          max_file_size_bytes: MAX_FILE_SIZE_BYTES,
          // 상한을 둬 프로퍼티가 무한정 커지지 않게 한다.
          rejected_sizes: oversized.slice(0, MAX_REPORTED_FILES).map((f) => f.size),
          // 무엇이 왜 걸렸는지 사후에 특정할 수 있도록 원인 재료를 함께 남긴다.
          // `File.type` 은 OS 가 준 값이라 빈 문자열이 정상적으로 올 수 있다 — 그대로 구분해 기록한다.
          rejected_unsupported_mime_types: unsupported.slice(0, MAX_REPORTED_FILES).map((f) => f.type || "(empty)"),
          rejected_unsupported_extensions: unsupported.slice(0, MAX_REPORTED_FILES).map((f) => fileExtension(f.name)),
          // accept 에 확장자 패턴을 넣은 뒤로는 0 이어야 한다. 0 이 아니면 원인이 MIME 가 아니라는 뜻.
          rejected_unsupported_heic_count: unsupported.filter((f) => hasHeicExtension(f.name)).length,
        });
      }

      // accept 확장자 패턴 덕에 통과한 파일들 — Windows/iOS 에서 MIME 가 비어 오는 HEIC.
      // 이 값이 0 보다 크면 "MIME 누락이 원인"이라는 가설이 실제로 재현된 것이다.
      const heicMissingMime = queued.filter((f) => isHeicWithMissingMime(f));

      // 이 선택에서 HEIC 가 하나라도 거부됐다면 accept 정합성이 깨진 것이다.
      // 계측만으로는 배포 후에야 알 수 있으므로 즉시 알 수 있게 Sentry 로도 올린다.
      if (unsupported.some((f) => hasHeicExtension(f.name))) {
        Sentry.captureMessage("HEIC file rejected by upload accept filter", {
          level: "warning",
          extra: {
            accept: ACCEPTED_IMAGE_TYPES,
            files: unsupported.slice(0, MAX_REPORTED_FILES).map((f) => ({ name: f.name, type: f.type, size: f.size })),
          },
        });
      }

      if (next.length > 0) {
        if (heicMissingMime.length > 0) {
          Sentry.addBreadcrumb({
            category: "image-upload",
            level: "info",
            message: "HEIC with missing MIME accepted by extension pattern",
            data: {
              count: heicMissingMime.length,
              extensions: heicMissingMime.slice(0, MAX_REPORTED_FILES).map((f) => fileExtension(f.name)),
            },
          });
        }

        EventLogger.track(CLIENT_EVENT.IMAGE_UPLOAD_START, {
          project_id: analyticsRef.current.projectId,
          draft_id: analyticsRef.current.draftId ?? null,
          upload_session_id: uploadSessionId,
          selected_count: all.length,
          queued_count: queued.length,
          heic_count: next.filter((f) => isHeicFile(f)).length,
          // 가설 확인용 핵심 지표: 확장자는 HEIC 인데 브라우저가 이미지 MIME 를 못 준 파일 수.
          // 수정 전이라면 전부 거부됐을 파일들이다. 뒤따르는 image_upload_success 까지 이어지면
          // "MIME 누락 → accept 탈락" 이 원인이었고 수정이 통했다는 증거가 된다.
          heic_missing_mime_count: heicMissingMime.length,
          empty_mime_count: next.filter((f) => f.type === "").length,
          total_bytes: next.reduce((sum, f) => sum + f.size, 0),
          already_committed_count: committedCount,
        });

        // 큐가 id 를 스스로 만들기 때문에, 추가 전후 스냅샷을 비교해 이번 선택에 속한 아이템을 가린다.
        // `onItemStart` 는 `addFiles` 안에서 동기적으로 불릴 수 있지만 세션 id 를 읽지 않고,
        // 세션을 읽는 완료/실패 콜백은 항상 XHR 이후이므로 이 시점 등록으로 충분하다.
        const idsBefore = new Set(uploadQueue.getSnapshot().map((queued) => queued.id));
        uploadQueue.addFiles(next);
        for (const queued of uploadQueue.getSnapshot()) {
          if (!idsBefore.has(queued.id)) uploadSessionsRef.current.set(queued.id, uploadSessionId);
        }
      }
    },
    [items, uploadQueue],
  );

  const removeItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id);
      // 큐에 있으면 (업로드 중/대기) XHR abort + blob revoke. 없으면 (existing/uploaded) no-op.
      uploadQueue.removeItem(id);
      uploadAttemptsRef.current.delete(id);
      uploadSessionsRef.current.delete(id);
      if (item?.lifecycle === "uploaded" && item.localPreviewUrl) {
        URL.revokeObjectURL(item.localPreviewUrl);
      }
      remove(id);
    },
    [uploadQueue, remove],
  );

  const retryItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id);
      EventLogger.click(CLIENT_EVENT.IMAGE_UPLOAD_RETRY, {
        project_id: analyticsRef.current.projectId,
        draft_id: analyticsRef.current.draftId ?? null,
        upload_session_id: uploadSessionsRef.current.get(id) ?? null,
        attempt: uploadAttemptsRef.current.get(id)?.attempt ?? 1,
        file_size: item?.file?.size ?? null,
        is_heic: item?.isHeic ?? null,
      });
      uploadQueue.retryItem(id);
    },
    [uploadQueue],
  );

  const releaseLocalPreview = useCallback(
    (id: string, localPreviewUrl: string) => {
      const item = items.find((it) => it.id === id);
      if (item?.localPreviewUrl !== localPreviewUrl) return;
      URL.revokeObjectURL(localPreviewUrl);
      clearLocalPreview(id);
    },
    [items, clearLocalPreview],
  );

  return {
    items,
    addFiles,
    removeItem,
    retryItem,
    releaseLocalPreview,
    isAtMax: items.length >= MAX_IMAGES,
  };
}
